// Admin Rights and Permission System for Nova Cafe
// Strategic oversight without operational interference

export interface AdminPermissionMatrix {
  // Strategic Management
  strategic: {
    viewBusinessMetrics: boolean;
    accessFinancialReports: boolean;
    manageSystemSettings: boolean;
    viewAuditLogs: boolean;
    configurePermissions: boolean;
  };
  
  // Staff Oversight (Non-Impersonation)
  staffOversight: {
    viewPerformanceMetrics: boolean;
    monitorActivity: boolean;
    viewSchedules: boolean;
    approveTimeOff: boolean;
    conductPerformanceReviews: boolean;
    viewStaffReports: boolean;
    managePayroll: boolean;
  };
  
  // Kitchen Monitoring (View-Only)
  kitchenMonitoring: {
    viewKitchenDisplay: boolean;
    monitorOrderFlow: boolean;
    viewPreparationTimes: boolean;
    accessKitchenMetrics: boolean;
    viewIngredientUsage: boolean;
    monitorFoodSafety: boolean;
  };
  
  // Order Management (Strategic Level)
  orderManagement: {
    cancelOrders: boolean;
    modifyOrders: boolean;
    refundOrders: boolean;
    viewOrderHistory: boolean;
    accessCustomerData: boolean;
    // Explicitly NOT allowed
    markOrdersReady: false; // Operational task for kitchen staff
    serveOrders: false; // Operational task for wait staff
  };
  
  // Emergency Overrides
  emergencyOverrides: {
    systemLockdown: boolean;
    emergencyRefunds: boolean;
    overridePriceLimits: boolean;
    bypassApprovals: boolean;
    accessEmergencyControls: boolean;
  };
  
  // Fraud Prevention
  fraudPrevention: {
    flagSuspiciousActivity: boolean;
    freezeAccounts: boolean;
    investigateTransactions: boolean;
    accessSecurityLogs: boolean;
    reviewDiscrepancies: boolean;
  };
  
  // Multi-Level Approvals
  approvals: {
    requireApprovalForRefunds: boolean;
    requireApprovalForDiscounts: boolean;
    requireApprovalForVoids: boolean;
    requireSecondSignature: boolean;
    escalateToOwner: boolean;
  };
}

// Action Types for Audit Logging
export enum AdminActionType {
  // Strategic Actions
  VIEW_FINANCIAL_REPORT = 'view_financial_report',
  MODIFY_SYSTEM_SETTINGS = 'modify_system_settings',
  ACCESS_AUDIT_LOG = 'access_audit_log',
  
  // Staff Oversight
  VIEW_STAFF_PERFORMANCE = 'view_staff_performance',
  APPROVE_TIME_OFF = 'approve_time_off',
  CONDUCT_REVIEW = 'conduct_review',
  
  // Kitchen Monitoring
  VIEW_KITCHEN_METRICS = 'view_kitchen_metrics',
  MONITOR_ORDER_FLOW = 'monitor_order_flow',
  
  // Order Management
  CANCEL_ORDER = 'cancel_order',
  MODIFY_ORDER = 'modify_order',
  PROCESS_REFUND = 'process_refund',
  
  // Emergency Actions
  EMERGENCY_OVERRIDE = 'emergency_override',
  SYSTEM_LOCKDOWN = 'system_lockdown',
  
  // Fraud Prevention
  FLAG_SUSPICIOUS_ACTIVITY = 'flag_suspicious_activity',
  FREEZE_ACCOUNT = 'freeze_account',
  INVESTIGATE_TRANSACTION = 'investigate_transaction',
}

// Audit Log Entry
export interface AuditLogEntry {
  id: string;
  timestamp: Date;
  adminId: string;
  adminName: string;
  action: AdminActionType;
  resource: string;
  resourceId?: string;
  details: Record<string, any>;
  ipAddress: string;
  userAgent: string;
  sessionId: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'strategic' | 'oversight' | 'operational' | 'emergency' | 'fraud';
  requiresSecondaryApproval?: boolean;
  approvedBy?: string;
  approvedAt?: Date;
  notes?: string;
}

// Performance Metrics for Staff Oversight
export interface StaffPerformanceMetrics {
  employeeId: string;
  employeeName: string;
  role: string;
  period: {
    start: Date;
    end: Date;
  };
  metrics: {
    // Service Metrics
    ordersProcessed: number;
    averageOrderTime: number;
    customerSatisfactionScore: number;
    
    // Attendance Metrics
    hoursWorked: number;
    scheduledHours: number;
    attendanceRate: number;
    punctualityScore: number;
    
    // Quality Metrics
    errorRate: number;
    complaintCount: number;
    commendationCount: number;
    
    // Financial Metrics
    salesGenerated: number;
    averageTicketSize: number;
    upsellSuccess: number;
  };
  trends: {
    improving: string[];
    declining: string[];
    stable: string[];
  };
  flags: {
    type: 'performance' | 'attendance' | 'conduct' | 'fraud';
    severity: 'low' | 'medium' | 'high';
    description: string;
    flaggedAt: Date;
  }[];
}

// Kitchen Monitoring Data (View-Only)
export interface KitchenMonitoringData {
  timestamp: Date;
  activeOrders: number;
  averagePreparationTime: number;
  delayedOrders: number;
  kitchenEfficiency: number;
  ingredientAlerts: {
    item: string;
    currentStock: number;
    minimumRequired: number;
    severity: 'low' | 'medium' | 'high';
  }[];
  temperatureMonitoring: {
    station: string;
    temperature: number;
    acceptable: boolean;
    lastChecked: Date;
  }[];
  staffActivity: {
    chefId: string;
    chefName: string;
    currentTask: string;
    ordersAssigned: number;
    efficiency: number;
  }[];
}

// Fraud Detection Alerts
export interface FraudAlert {
  id: string;
  timestamp: Date;
  type: 'suspicious_transaction' | 'unusual_pattern' | 'system_anomaly' | 'staff_behavior';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  involvedEntities: {
    employees?: string[];
    orders?: string[];
    transactions?: string[];
    customers?: string[];
  };
  indicators: {
    name: string;
    value: any;
    threshold: any;
    confidence: number;
  }[];
  status: 'new' | 'investigating' | 'resolved' | 'false_positive';
  investigatedBy?: string;
  resolution?: string;
  resolutionDate?: Date;
}

// Multi-Level Approval Request
export interface ApprovalRequest {
  id: string;
  requestedBy: string;
  requestedAt: Date;
  action: AdminActionType;
  resource: string;
  resourceId: string;
  amount?: number;
  reason: string;
  justification: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  
  approvalLevels: {
    level: number;
    role: 'manager' | 'restaurant_owner' | 'owner';
    required: boolean;
    approvedBy?: string;
    approvedAt?: Date;
    denied?: boolean;
    deniedReason?: string;
  }[];
  
  status: 'pending' | 'approved' | 'denied' | 'escalated';
  completedAt?: Date;
  
  // Emergency override
  emergencyOverride?: {
    triggeredBy: string;
    reason: string;
    timestamp: Date;
    requiresPostApproval: boolean;
  };
}

// System Security Context
export interface SecurityContext {
  adminId: string;
  sessionId: string;
  ipAddress: string;
  userAgent: string;
  loginTime: Date;
  lastActivity: Date;
  permissions: AdminPermissionMatrix;
  
  // Security Features
  mfaEnabled: boolean;
  sessionTimeout: number;
  maxConcurrentSessions: number;
  requiresReauthentication: boolean;
  
  // Risk Assessment
  riskScore: number;
  riskFactors: string[];
  
  // Audit Trail
  actionsPerformed: number;
  sensitiveActionsCount: number;
  lastAuditReview: Date;
}

// Default Admin Permission Matrix
export const DEFAULT_ADMIN_PERMISSIONS: AdminPermissionMatrix = {
  strategic: {
    viewBusinessMetrics: true,
    accessFinancialReports: true,
    manageSystemSettings: true,
    viewAuditLogs: true,
    configurePermissions: true,
  },
  staffOversight: {
    viewPerformanceMetrics: true,
    monitorActivity: true,
    viewSchedules: true,
    approveTimeOff: true,
    conductPerformanceReviews: true,
    viewStaffReports: true,
    managePayroll: true,
  },
  kitchenMonitoring: {
    viewKitchenDisplay: true,
    monitorOrderFlow: true,
    viewPreparationTimes: true,
    accessKitchenMetrics: true,
    viewIngredientUsage: true,
    monitorFoodSafety: true,
  },
  orderManagement: {
    cancelOrders: true,
    modifyOrders: true,
    refundOrders: true,
    viewOrderHistory: true,
    accessCustomerData: true,
    markOrdersReady: false, // Explicitly forbidden
    serveOrders: false, // Explicitly forbidden
  },
  emergencyOverrides: {
    systemLockdown: true,
    emergencyRefunds: true,
    overridePriceLimits: true,
    bypassApprovals: true,
    accessEmergencyControls: true,
  },
  fraudPrevention: {
    flagSuspiciousActivity: true,
    freezeAccounts: true,
    investigateTransactions: true,
    accessSecurityLogs: true,
    reviewDiscrepancies: true,
  },
  approvals: {
    requireApprovalForRefunds: true,
    requireApprovalForDiscounts: true,
    requireApprovalForVoids: true,
    requireSecondSignature: true,
    escalateToOwner: true,
  },
};